
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import {
  Search, 
  Plus, 
  FileEdit, 
  Trash, 
  Loader2, 
  X,
  User,
  Users,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { categoriesAPI } from "@/services/api";
import { format } from "date-fns";

export default function Categories() {
  const PAGE_LIMIT = 5; // Single constant for items per page
  
  // Search states for each section
  const [mySearchTerm, setMySearchTerm] = useState("");
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  
  // Pagination states for each section
  const [myPage, setMyPage] = useState(1);
  const [companyPage, setCompanyPage] = useState(1);
  
  // Sorting states for each section
  const [mySortField, setMySortField] = useState('');
  const [mySortOrder, setMySortOrder] = useState('asc');
  const [companySortField, setCompanySortField] = useState('');
  const [companySortOrder, setCompanySortOrder] = useState('asc');
  
  // Modal and form states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [newSubCategory, setNewSubCategory] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm({
    defaultValues: {
      name: "",
      description: "",
      active: true,
    }
  });

  const editForm = useForm({
    defaultValues: {
      name: "",
      description: "",
      active: true,
    }
  });

  // Query to get all categories data with backend pagination
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['categories', myPage, companyPage, PAGE_LIMIT, mySearchTerm, companySearchTerm],
    queryFn: () => categoriesAPI.getAllCat({ 
      myPage, 
      myLimit: PAGE_LIMIT, 
      mySearch: mySearchTerm,
      companyPage,
      companyLimit: PAGE_LIMIT,
      companySearch: companySearchTerm
    }),
    keepPreviousData: true,
  });

  // ... keep existing code (mutations)
  const createMutation = useMutation({
    mutationFn: (newCategory) => categoriesAPI.create(newCategory),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      setIsAddModalOpen(false);
      toast({
        title: "Success",
        description: "Category created successfully",
      });
      resetForm();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to create category",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => categoriesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      setIsEditModalOpen(false);
      setCurrentCategory(null);
      toast({
        title: "Success",
        description: "Category updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update category",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => categoriesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      toast({
        title: "Success",
        description: "Category deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete category",
        variant: "destructive",
      });
    },
  });

  // Form population for edit
  useEffect(() => {
    if (currentCategory) {
      editForm.setValue("name", currentCategory.name || "");
      editForm.setValue("description", currentCategory.description || "");
      editForm.setValue("active", currentCategory.active !== false);
      
      const subCategoryNames = currentCategory.subCategories?.map(sub => 
        typeof sub === 'string' ? sub : sub.name
      ) || [];
      setSubCategories(subCategoryNames);
    }
  }, [currentCategory, editForm]);

  // Search handlers
  const handleMySearch = (e) => {
    e.preventDefault();
    setMyPage(1); // Reset to first page when searching
    // Clear sorting when search changes to show newest first
    setMySortField('');
    setMySortOrder('asc');
    refetch();
  };

  const handleCompanySearch = (e) => {
    e.preventDefault();
    setCompanyPage(1); // Reset to first page when searching
    // Clear sorting when search changes to show newest first
    setCompanySortField('');
    setCompanySortOrder('asc');
    refetch();
  };

  // Sorting functions
  const handleMySort = (field) => {
    if (mySortField === field) {
      setMySortOrder(mySortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setMySortField(field);
      setMySortOrder('asc');
    }
  };

  const handleCompanySort = (field) => {
    if (companySortField === field) {
      setCompanySortOrder(companySortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setCompanySortField(field);
      setCompanySortOrder('asc');
    }
  };

  const getMySortIcon = (field) => {
    if (mySortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return mySortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  const getCompanySortIcon = (field) => {
    if (companySortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return companySortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  const sortCategories = (categories, sortField, sortOrder) => {
    // If no sorting is applied, return categories in their original order (newest first from API)
    // This ensures newly created categories appear at the top across all pages
    if (!sortField) return categories;

    return [...categories].sort((a, b) => {
      let aValue, bValue;

      // Handle nested properties
      if (sortField.includes('.')) {
        const fields = sortField.split('.');
        aValue = fields.reduce((obj, field) => obj?.[field], a);
        bValue = fields.reduce((obj, field) => obj?.[field], b);
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      // Handle date fields
      if (sortField === 'createdDate') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'asc' ? -1 : 1;

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Helper functions
  const resetForm = () => {
    form.reset();
    setSubCategories([]);
    setNewSubCategory("");
  };

  const handleAddSubCategory = () => {
    if (newSubCategory.trim() && !subCategories.includes(newSubCategory.trim())) {
      setSubCategories([...subCategories, newSubCategory.trim()]);
      setNewSubCategory("");
    }
  };

  const handleRemoveSubCategory = (index) => {
    const newSubCategories = [...subCategories];
    newSubCategories.splice(index, 1);
    setSubCategories(newSubCategories);
  };

 const onSubmit = (formData) => {
    // Trim form data
    const trimmedData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      active: formData.active,
      subCategories: subCategories.map(sub => sub.trim()).filter(sub => sub.length > 0)
    };
    createMutation.mutate(trimmedData);
  };

  const onEditSubmit = (formData) => {
    // Trim form data
    const trimmedData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      active: formData.active,
      subCategories: subCategories.map(sub => sub.trim()).filter(sub => sub.length > 0)
    };
    
    updateMutation.mutate({
      id: currentCategory._id,
      data: trimmedData
    });
  };

  const handleEdit = (category) => {
    setCurrentCategory(category);
    setIsEditModalOpen(true);
  };

  const handleDeleteCategory = async (id) => {
    if (!confirm("Are you sure you want to delete this category?")) return;
    
    try {
      deleteMutation.mutate(id);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete category",
        variant: "destructive",
      });
    }
  };

  // Render pagination component
  const renderPagination = (pagination, currentPage, setPage) => {    
    if (!pagination || pagination.totalPages <= 1) return null;
    const { totalPages } = pagination;

    return (
      <Pagination className="mt-4">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious 
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>
          
          {/* Show all page numbers */}
          {[...Array(totalPages).keys()].map((i) => {
            const pageNumber = i + 1;
            return (
              <PaginationItem key={pageNumber}>
                <PaginationLink 
                  isActive={currentPage === pageNumber}
                  onClick={() => setPage(pageNumber)}
                  className={`cursor-pointer ${
                    currentPage === pageNumber
                      ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                      : ""
                  }`}
                >
                  {pageNumber}
                </PaginationLink>
              </PaginationItem>
            );
          })}
          
          <PaginationItem>
            <PaginationNext 
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    );
  };

  // Render category table
  const renderCategoryTable = (categoriesData, title, icon, searchTerm, setSearchTerm, onSearch, currentPage, setPage, sortField, sortOrder, handleSort, getSortIcon) => {
    
    const categories = categoriesData?.data || [];
    const pagination = categoriesData?.pagination || {};

    // Sort the categories
    const sortedCategories = sortCategories(categories, sortField, sortOrder);

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          {icon}
          <h3 className="text-lg font-semibold">{title}</h3>
          <span className="text-sm text-gray-500">({pagination.total || 0})</span>
        </div>
        
        <form onSubmit={onSearch} className="mb-4 flex items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              type="search"
              placeholder="Search by category name or description..."
              className="pl-8 w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button type="submit" size="sm">Search</Button>
        </form>
        
        {categories && categories.length > 0 ? (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>S.No</TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Name
                        {getSortIcon('name')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('description')}
                    >
                      <div className="flex items-center">
                        Description
                        {getSortIcon('description')}
                      </div>
                    </TableHead>
                    <TableHead>Subcategories</TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('active')}
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon('active')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('createdBy.name')}
                    >
                      <div className="flex items-center">
                        Created By
                        {getSortIcon('createdBy.name')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('createdDate')}
                    >
                      <div className="flex items-center">
                        Created On
                        {getSortIcon('createdDate')}
                      </div>
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedCategories.map((category, index) => (
                    <TableRow key={category._id}>
                      <TableCell className="font-medium">{(currentPage - 1) * PAGE_LIMIT + index + 1}</TableCell>
                      <TableCell className="font-medium">{category.name}</TableCell>
                      <TableCell>{category.description || "N/A"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {category.subCategories && category.subCategories.length > 0 ? (
                            category.subCategories.map((sub, index) => (
                              <Badge key={index} variant="outline">
                                {typeof sub === 'string' ? sub : sub.name}
                              </Badge>
                            ))
                          ) : (
                            "None"
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={category.active ? "default" : "secondary"}>
                          {category.active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>{category.createdBy?.name || "N/A"}</TableCell>
                      <TableCell>
                        {category.createdDate ? format(new Date(category.createdDate), 'MMM dd, yyyy') : 'N/A'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            title="Edit category"
                            onClick={() => handleEdit(category)}
                          >
                            <FileEdit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteCategory(category._id)}
                            title="Delete category"
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Render pagination */}
            {renderPagination(pagination, currentPage, setPage)}
          </>
        ) : (
          <div className="text-center p-6 text-gray-500 border rounded-md">
            No categories found in this section
          </div>
        )}
      </div>
    );
  };

  // Extract data based on the actual backend response structure
  const userCategoriesData = data?.data?.data?.userCategories || { data: [], pagination: {} };
  const companyCategoriesData = data?.data?.data?.companyCategories || { data: [], pagination: {} };


  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Categories</h1>
        <Button onClick={() => setIsAddModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary border-t-primary"></div>
            </div>
          ) : isError ? (
            <div className="text-center p-6 text-red-500">
              Failed to load categories. Please try again.
            </div>
          ) : (
            <Accordion type="multiple" defaultValue={["my-categories", "company-categories"]} className="w-full">
              <AccordionItem value="my-categories">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    <span>My Categories ({userCategoriesData?.pagination?.total || 0})</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {renderCategoryTable(
                    userCategoriesData, 
                    "Categories created by you", 
                    <User className="h-5 w-5 text-blue-600" />,
                    mySearchTerm,
                    setMySearchTerm,
                    handleMySearch,
                    myPage,
                    setMyPage,
                    mySortField,
                    mySortOrder,
                    handleMySort,
                    getMySortIcon
                  )}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="company-categories">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>Company Categories ({companyCategoriesData?.pagination?.total || 0})</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {renderCategoryTable(
                    companyCategoriesData, 
                    "Categories created by other company users", 
                    <Users className="h-5 w-5 text-green-600" />,
                    companySearchTerm,
                    setCompanySearchTerm,
                    handleCompanySearch,
                    companyPage,
                    setCompanyPage,
                    companySortField,
                    companySortOrder,
                    handleCompanySort,
                    getCompanySortIcon
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* ... keep existing code (Add Category Dialog, Edit Category Dialog) */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                rules={{ required: "Category name is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter category name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter category description" 
                        className="resize-none" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <FormLabel>Subcategories</FormLabel>
                <div className="flex space-x-2">
                  <Input
                    placeholder="Enter subcategory"
                    value={newSubCategory}
                    onChange={(e) => setNewSubCategory(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSubCategory();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button type="button" onClick={handleAddSubCategory} size="icon">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2 mt-2">
                  {subCategories.map((subCategory, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {subCategory}
                      <button 
                        type="button" 
                        onClick={() => handleRemoveSubCategory(index)}
                        className="hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {subCategories.length === 0 && (
                    <p className="text-sm text-gray-500">No subcategories added yet</p>
                  )}
                </div>
              </div>

              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Status</FormLabel>
                      <div className="text-sm text-gray-500">
                        Set the category as active or inactive
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setIsAddModalOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isLoading}>
                  {createMutation.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Add Category
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditModalOpen} onOpenChange={(open) => {
        setIsEditModalOpen(open);
        if (!open) {
          setCurrentCategory(null);
          setSubCategories([]);
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          <Form {...editForm}>
            <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-4">
              <FormField
                control={editForm.control}
                name="name"
                rules={{ required: "Category name is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter category name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter category description" 
                        className="resize-none" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <FormLabel>Subcategories</FormLabel>
                <div className="flex space-x-2">
                  <Input
                    placeholder="Enter subcategory"
                    value={newSubCategory}
                    onChange={(e) => setNewSubCategory(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSubCategory();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button type="button" onClick={handleAddSubCategory} size="icon">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2 mt-2">
                  {subCategories.map((subCategory, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {subCategory}
                      <button 
                        type="button" 
                        onClick={() => handleRemoveSubCategory(index)}
                        className="hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {subCategories.length === 0 && (
                    <p className="text-sm text-gray-500">No subcategories added yet</p>
                  )}
                </div>
              </div>

              <FormField
                control={editForm.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Status</FormLabel>
                      <div className="text-sm text-gray-500">
                        Set the category as active or inactive
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setCurrentCategory(null);
                    setSubCategories([]);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={updateMutation.isLoading}>
                  {updateMutation.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Update Category
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
